import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthProvider'
import { ToastProvider } from './contexts/ToastContext'
import App from './App.tsx'
import './index.css'

// Suppress browser extension errors that pollute the console
const suppressBrowserExtensionErrors = () => {
  // Handle unhandled promise rejections from browser extensions
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || event.reason || '';
    const errorString = String(errorMessage);
    
    // Check if it's a browser extension error
    if (
      errorString.includes('message port closed') ||
      errorString.includes('message channel closed') ||
      errorString.includes('runtime.lastError') ||
      errorString.includes('asynchronous response') ||
      errorString.includes('listener indicated an asynchronous response')
    ) {
      event.preventDefault();
      // Silently ignore - these are from browser extensions, not our code
      return;
    }
  });

  // Handle console errors from browser extensions (Chrome/Edge)
  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    const errorString = args.map(arg => String(arg)).join(' ');
    
    // Filter out browser extension errors
    if (
      errorString.includes('runtime.lastError') ||
      errorString.includes('message port closed') ||
      errorString.includes('message channel closed')
    ) {
      // Silently ignore browser extension errors
      return;
    }
    
    // Log all other errors normally
    originalError.apply(console, args);
  };

  // Handle runtime.lastError warnings (Chrome extensions)
  const w = typeof window !== 'undefined'
    ? (window as unknown as { chrome?: { runtime?: { lastError?: { message?: string } } } })
    : undefined;
  const runtime = w?.chrome?.runtime;
  if (runtime) {
    const originalLastError = runtime.lastError;
    Object.defineProperty(runtime, 'lastError', {
      get: function() {
        const error = originalLastError;
        if (error && !error.message?.includes('message port') && !error.message?.includes('message channel')) {
          return error;
        }
        return undefined;
      },
      configurable: true,
    });
  }
};


// Initialize error suppression before rendering
suppressBrowserExtensionErrors();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
      staleTime: 0, // Данные считаются устаревшими сразу, чтобы всегда получать актуальную информацию
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
